import styles from "./styles.module.scss";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import InputField from "./InputField";
import SpeakerField from "./SpeakerField";
import FileUpload from "./FileUpload";
import CryptoJS from "crypto-js";
import axios from "axios";
import { initiateTranscript } from "../../../api/transcriptsApi";
import { Box } from "@mui/system";
import { LinearProgress, Link, Typography, Tabs, Tab } from "@mui/material";
import { fetchEventsWithChildren, fetchChildEvents } from "../../../api/wpApi";
import { fetchFaiconEvents } from "../../../api/faiconApi";
import { extractUniqueDatesWithEventIDs } from "../../../services/DateExtractor";
import swal from "sweetalert";
import TagOptions from "./TagOptions";
import { fetchFaiconSessions } from "../../../api/faiconApi";
import { extractUniqueDays, htmlToText } from "../../../services/FaiconHelpers";

const FAICON_API_KEY = process.env.REACT_APP_FAICON_APP_TOKEN;
const FAICON_API_URL = process.env.REACT_APP_FAICON_APP_URL;
const FAICON_API_EXISTS = FAICON_API_KEY && FAICON_API_URL;

export default function TranscriptUploader() {
  const [file, setFile] = useState(null);
  const formDataInitialState = useMemo(
    () => ({
      user_id: "",
      file_id: "",
      file_type: "",
      event_name: "",
      event_day: "",
      event_id: "",
      title: "",
      tags: [],
      wp_id: "",
      upload_date: "",
      description: "",
      moderator: "",
      speakers: [],
      faicon_session_id: "",
      transcript_status: "",
      transcript: {},
      report_status: "",
      report: {},
      report_url: "",
    }),
    []
  );
  const [formData, setFormData] = useState(formDataInitialState);
  const user_id = "123";
  const [error, setError] = useState(null);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [typeOfTitle, setTypeOfTitle] = useState("WordPress");
  const [speakers, setSpeakers] = useState([""]);
  const [isLoading, setIsLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_FILE_UPLOAD_HOST;
  const BACKEND_PORT = process.env.REACT_APP_FILE_UPLOAD_PORT;

  // Reset state function
  const resetState = useCallback(() => {
    setDays([]);
    setSessions([]);
    setSelectedEvent(null);
    setSelectedDay(null);
    setSpeakers([""]);
    setFormData(formDataInitialState);
  }, [formDataInitialState]);

  // Set user_id, uploaded date and initial status in formData
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      user_id: user_id,
    }));
  }, [user_id, resetState]);

  // Get events from WordPress or Faicon APP
  useEffect(() => {
    resetState();
    if (typeOfTitle !== "Custom") {
      const fetchData = async () => {
        const eventsData =
          typeOfTitle === "WordPress"
            ? await fetchEventsWithChildren()
            : await fetchFaiconEvents();
        setEvents(eventsData || []);
      };
      fetchData();
    } else {
      setEvents([]);
    }
  }, [typeOfTitle, resetState]);

  const handleEventChange = async (eventID, eventName) => {
    resetState();
    setIsLoading(true);
    setSelectedEvent(eventID);
    const daysData =
      typeOfTitle === "WordPress"
        ? await fetchChildEvents(eventID).then((data) => {
            setIsLoading(false);
            return data;
          })
        : await fetchFaiconSessions(eventID).then((data) => {
            setIsLoading(false);
            return data;
          });
    const days =
      typeOfTitle === "WordPress"
        ? extractUniqueDatesWithEventIDs(daysData)
        : extractUniqueDays(daysData);
    typeOfTitle === "FaiconAPP" && setSessions(daysData);
    setDays(days);
    setFormData((prevData) => ({
      ...prevData,
      event_id: eventID,
      event_name: eventName,
    }));
  };

  const handleDayChange = (selectedDay) => {
    setSelectedDay(selectedDay);
    setFormData((prevData) => ({
      ...prevData,
      event_day: selectedDay,
      faicon_session_id: "",
      wp_id: "",
      title: "",
      speakers: [""],
      moderator: "",
      description: "",
    }));
    if (typeOfTitle === "WordPress") {
      const dayObject = days.find((dayObj) => dayObj.day === selectedDay);
      if (dayObject) {
        setSessions(dayObject.events);
      } else {
        setSessions([]);
      }
    } else if (typeOfTitle === "FaiconAPP") {
      const filteredSessions = sessions.filter(
        (session) => session.start_date.split("T")[0] === selectedDay
      );
      if (filteredSessions.length > 0) {
        setSessions(filteredSessions);
      } else {
        setSessions([]);
      }
    }
  };

  const handleSessionChange = (selectedSessionID, selectedSessionTitle) => {
    if (typeOfTitle === "WordPress") {
        const session = sessions.find(
          (session) => session.id === selectedSessionID
        );
      setSpeakers(session?.speakers ? session.speakers : [""]);
      setFormData((prevData) => ({
        ...prevData,
        wp_id: selectedSessionID,
        title: selectedSessionTitle,
        speakers: session?.speakers ? session.speakers : [""],
      }));
    } else if (typeOfTitle === "FaiconAPP") {
      const session = sessions.find(
        (session) => session.id === selectedSessionID
      );
      if (session) {
        const description = htmlToText(session.description);
        const moderator =
          session.moderator_list.length > 0
            ? session.moderator_list.map((moderator) => moderator.first_name + " " + moderator.last_name).join(", ")
            : "";
        const sessionSpeakers = session.speaker_list?.map((speaker) => {
          return `${speaker.first_name} ${speaker.last_name}`;
        }) || [""];
        const speakers = sessionSpeakers.length > 0 ? sessionSpeakers : [""];
        setSpeakers(speakers);
        setFormData((prevData) => ({
          ...prevData,
          faicon_session_id: selectedSessionID,
          title: selectedSessionTitle,
          description: description,
          moderator: moderator,
          speakers: speakers,
        }));
      }
    }
  };

  // Updating data using form inputs
  const handleInputChange = (name, value) => {
    name === "event_id"
      ? setFormData((prevData) => ({
          ...prevData,
          [name]: CryptoJS.MD5(value).toString(),
          event_name: value,
        }))
      : setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  // Updating data using file upload
  const setMediaFiles = (fileNameHash, fileExtension) => {
    setFormData((prevData) => ({
      ...prevData,
      file_id: fileNameHash,
      file_type: fileExtension,
    }));
  };

  // Upload audio file to server
  const uploadMediaToServer = async (file) => {
    if (!file) {
      setError("Please select a file before submitting.");
      const response = { error: "Please select a file before submitting." };
      return response;
    }
    const toHash = file.name + user_id;
    const fileNameHash = CryptoJS.MD5(toHash).toString();
    const fileExtension = file.name.split(".").pop();
    const formData = new FormData();
    formData.append("media", file, `${fileNameHash}.${fileExtension}`);

    try {
      console.log("Uploading to:", `${BACKEND_URL}:${BACKEND_PORT}/uploads/${user_id}`);
      await axios.post(
        `${BACKEND_URL}:${BACKEND_PORT}/uploads/${user_id}`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            let percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadPercentage(percentCompleted);
          },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setError("File uploaded successfully.");
      return { success: "File uploaded successfully." };
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      setError("Error uploading file: " + (error.response?.data || error.message));
    }
  };

  // Submit form data to server
  const handleSubmit = async (e, file) => {
    e.preventDefault();
    await uploadMediaToServer(file).then((response) => {
      if (response?.success) {
        initiateTranscript(formData).then((response) => {
          if (response?.transcript_status && response?.transcript_status !== "") {
            setUploaded(true);
            swal(
              "Transcript uploaded",
              `Your transcript has been uploaded successfully! Transcript is ${response.transcript_status}`,
              "success"
            );
          }
        });
      } else {
        console.log(response?.error);
      }
    });
  };

  const [tags, setTags] = useState([]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      tags: tags,
    }));
  }, [tags, setFormData]);

  return (
    <div className={styles.transcriptUploader}>
      <h1>Upload new audio transcript</h1>
      <form
        className={styles.uploadForm}
        name="uploadTranscript"
        onSubmit={(e) => handleSubmit(e, file)}
      >
        <Tabs
          value={typeOfTitle}
          onChange={(event, newValue) => {
            setTypeOfTitle(newValue);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          <Tab
            label="WordPress Event"
            value="WordPress"
            style={{
              marginRight: 8,
              color: "#fff",
              fontWeight: 600,
              fontFamily: "Montserrat, sans-serif",
              textTransform: "unset",
              border: typeOfTitle === "WordPress" ? "3px solid #fff" : "none",
            }}
          />
          <Tab
            label="Custom Event"
            value="Custom"
            style={{
              marginLeft: 8,
              color: "#fff",
              fontWeight: 600,
              fontFamily: "Montserrat, sans-serif",
              textTransform: "unset",
              border: typeOfTitle === "Custom" ? "3px solid #fff" : "none",
            }}
          />
          <Tab
            label="Faicon Event"
            value="FaiconAPP"
            style={{
              marginLeft: 8,
              color: "#fff",
              fontWeight: 600,
              fontFamily: "Montserrat, sans-serif",
              textTransform: "unset",
              border: typeOfTitle === "FaiconAPP" ? "3px solid #fff" : "none",
              ...(!FAICON_API_EXISTS && {
                cursor: "not-allowed",
                opacity: 0.5,
              }),
            }}
            disabled={!FAICON_API_EXISTS}
          />
        </Tabs>
        {typeOfTitle === "Custom" && (
          <>
            <InputField
              label={"Event title"}
              name={"event_id"}
              onChange={handleInputChange}
            />
            <InputField
              label="Transcript Title"
              name="title"
              onChange={handleInputChange}
            />
          </>
        )}
        {typeOfTitle === "WordPress" && (
          <div key="wordpress-event-select" className={styles.uploadForm}>
            <select
              onChange={(e) =>
                handleEventChange(
                  e.target.value,
                  e.target.options[e.target.selectedIndex].dataset.title
                )
              }
              value={selectedEvent || ""}
              disabled={isLoading}
            >
              <option value="">Choose an event</option>
              {events.length > 0 &&
                events.map((event) => (
                  <option
                    key={event.ID}
                    value={event.ID}
                    data-title={event.post_title}
                  >
                    {event.post_title}
                  </option>
                ))}
            </select>
            <select
              onChange={(e) => handleDayChange(e.target.value)}
              value={selectedDay || ""}
            >
              <option value="">Choose a day</option>
              {days.map((day) => (
                <option key={day.day} value={day.day}>
                  {day.day}
                </option>
              ))}
            </select>
            <select
              onChange={(e) =>
                handleSessionChange(
                  e.target.value,
                  e.target.options[e.target.selectedIndex].dataset.title
                )
              }
            >
              <option value="">Select a session</option>
              {sessions.map((session) => (
                <option
                  key={session.id}
                  value={session.id}
                  data-title={session.title}
                >
                  {session.title}
                </option>
              ))}
            </select>
          </div>
        )}
        {typeOfTitle === "FaiconAPP" && (
          <div key="faicon-event-select" className={styles.uploadForm}>
            <select
              key={'faicon-event-select'}
              onChange={(e) =>
                handleEventChange(
                  e.target.value,
                  e.target.options[e.target.selectedIndex].dataset.title
                )
              }
              disabled={events.length === 0 || isLoading}
              value={selectedEvent || ""}
            >
              <option value="">
                {events.length === 0 ? "Loading..." : "Choose an event"}
              </option>
              {events.length > 0 &&
                events.map((event, index) => (
                  <option
                    key={index}
                    value={event.id}
                    data-title={event.name}
                  >
                    {event.name}
                  </option>
                ))}
            </select>
            <select
              key={'faicon-day-select'}
              onChange={(e) => handleDayChange(e.target.value)}
              disabled={days.length === 0 || !selectedEvent || isLoading}
              value={selectedDay || ""}
            >
              <option value="">
                {!selectedEvent
                  ? "---"
                  : isLoading
                  ? "Loading..."
                  : days.length === 0
                  ? "No days found"
                  : "Choose a day"}
              </option>
              {days?.map((day, index) => (
                <option key={index} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <select
              key={'faicon-session-select'}
              onChange={(e) =>
                handleSessionChange(
                  e.target.value,
                  e.target.options[e.target.selectedIndex].dataset.title
                )
              }
              disabled={sessions.length === 0 || !selectedEvent || isLoading}
            >
              <option value="">
                {!selectedEvent && !selectedDay
                  ? "---"
                  : isLoading
                  ? "Loading..."
                  : sessions.length === 0
                  ? "No sessions found"
                  : "Select a session"}
              </option>
              {sessions?.map((session, index) => (
                <option
                  key={index}
                  value={session.id}
                  data-title={session.name}
                >
                  {session.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <TagOptions
          tags={tags}
          setTags={setTags}
          availableTags={["Diplo Reporting Team", "Outsource"]}
        />

        <InputField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          type="textarea"
        />

        <div className={styles.fieldContainer}>
          <legend className={styles.fieldContainerLegend}>Moderator</legend>
          <InputField
            label="Moderator"
            name="moderator"
            value={formData.moderator}
            onChange={handleInputChange}
          />
        </div>

        <div className={styles.fieldContainer}>
          <legend className={styles.fieldContainerLegend}>Speakers</legend>
          <SpeakerField
            onChange={handleInputChange}
            speakers={speakers}
            setSpeakers={setSpeakers}
          />
        </div>

        <FileUpload
          file={file}
          setFile={setFile}
          setMediaFiles={setMediaFiles}
          user_id={user_id}
        />
        <button className={styles.submit} type="submit">
          Upload
        </button>
        {uploadPercentage > 0 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadPercentage} />
            <Typography variant="body2" align="center" mt={1}>
              {uploadPercentage}%
            </Typography>
          </Box>
        )}
        {(uploaded || uploadPercentage === 100) && (
          <Box sx={{ mt: 2, mx: "auto" }}>
            <Link href="/transcripts" variant="body1" color="inherit">
              The transcript is processing. Go to my transcripts to view the
              status.
            </Link>
          </Box>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </form>
    </div>
  );
}
